import React from "react";
import { useEffect, useRef, useCallback } from "react";
import { ProductCard } from "../Product/ProductCard";
import { Spinner as LoadingSpinner } from "../ui/Spinner";
import { Product } from "@nusrif/data";
import { getLowestPriceVariant, isProductInStock } from "@nusrif/services";
import { formatCurrency } from "@nusrif/utils";
import config from "@nusrif/config";

interface ProductGridProps {
  products: Product[];
  onProductClick: (productId: number) => void;
  onFavoriteClick: (productId: number) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  loading: boolean;
}

export const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  onProductClick,
  onFavoriteClick,
  onLoadMore,
  hasMore,
  loading,
}) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  // Intersection Observer for infinite scroll
  const lastProductElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            onLoadMore();
          }
        },
        {
          threshold: 0.1,
          rootMargin: "100px",
        }
      );

      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore, onLoadMore]
  );

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);
  return (
    <div className="space-y-6">
      {/* Products Grid */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {products.map((product, index) => {
          const lowestPriceVariant = getLowestPriceVariant(product);
          const price = lowestPriceVariant
            ? formatCurrency(
                lowestPriceVariant.priceWithTax,
                lowestPriceVariant.currencyCode
              )
            : formatCurrency(0, config.region.currencyCode);
          const mrpPrice = product.customFields?.mrpPrice
            ? formatCurrency(
                product.customFields.mrpPrice,
                config.region.currencyCode
              )
            : formatCurrency(0, config.region.currencyCode);
          const image =
            product.featuredAsset?.preview ||
            (product.assets && product.assets[0]?.preview) ||
            "/image.png";
          const brand =
            product.facetValues?.find((f) => f.facet.name === "Brand")?.name ||
            "Two & Five";
          const name = product.name || "Two And Five Product";
          const isInStock = isProductInStock(product);
          const productId = parseInt(product.id);
          return (
            <div
              key={`${product.id}-${index}`}
              ref={index === products.length - 1 ? lastProductElementRef : null}
            >
              <ProductCard
                id={productId}
                image={image}
                brand={brand}
                name={name}
                price={price}
                mrpPrice={mrpPrice}
                heartIcon={isInStock ? "/love.png" : "/love-1.png"}
                onFavoriteClick={onFavoriteClick}
                onProductClick={onProductClick}
              />
            </div>
          );
        })}
      </div>
      {/* Load More Indicator */}
      {hasMore && (
        <div ref={loadMoreRef} className="flex justify-center py-8">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <button
              onClick={onLoadMore}
              className="px-6 py-3 bg-primary-blue text-white rounded-full font-medium hover:bg-primary-blue/90 transition-colors"
            >
              Load More Products
            </button>
          )}
        </div>
      )}
      {/* End of Results */}
      {!hasMore && products.length > 0 && (
        <div className="text-center py-8">
          <p className="text-gray-600">You&apos;ve seen all products</p>
        </div>
      )}
    </div>
  );
};
